import { useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import {
  canRerunWholeWorkbook,
  canRunInitialPayroll,
  hasIcmPayrollViewerAccess,
  hasPayrollScriptAccess,
} from "@/core/business/payroll-only";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { useCreateAiScriptRun } from "@/payroll/ai-script/query";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  type AiScriptRerunRunOptions,
} from "@/payroll/pay-breakdown/types";
import {
  getLegacyRunFilename,
  getPayBreakdownRerunAction,
  getPayBreakdownRunTarget,
} from "@/payroll/pay-breakdown/utils";
import {
  PAYROLL_RUN_TYPE,
  useCreatePayPeriod,
  useTriggerPayrollRun,
} from "@/payroll/pay-periods/query";
import { downloadFile } from "@/payroll/utils/download-file";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type UsePayBreakdownRunActionsParams = {
  businessId: string | null;
  hasAiScript: boolean;
} & Pick<
  PayBreakdownPeriodState,
  | "business"
  | "output"
  | "selectedPayPeriod"
  | "selectedPeriod"
  | "selectedPeriodForCard"
  | "selectedAiRunId"
  | "isLocked"
  | "isFuturePeriod"
  | "hasActiveRun"
  | "hasActiveIcmPayrollRun"
  | "hasBlockingRun"
  | "hasEditableSheet"
  | "rerunUnits"
  | "icmLatestRunStatus"
  | "isBeforeBusinessCreated"
>;

export const usePayBreakdownRunActions = ({
  businessId,
  hasAiScript,
  business,
  output,
  selectedPayPeriod,
  selectedPeriod,
  selectedPeriodForCard,
  selectedAiRunId,
  isLocked,
  isFuturePeriod,
  hasActiveRun,
  hasActiveIcmPayrollRun,
  hasBlockingRun,
  hasEditableSheet,
  rerunUnits,
  icmLatestRunStatus,
  isBeforeBusinessCreated,
}: UsePayBreakdownRunActionsParams) => {
  const [aiScriptRerunConfirmOpen, setAiScriptRerunConfirmOpen] =
    useState(false);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const { mutate: createAiRun, isPending: isCreatingAiRun } =
    useCreateAiScriptRun(businessId);
  const { mutate: createPeriod, isPending: isCreatingPeriod } =
    useCreatePayPeriod(businessId);
  const { mutate: triggerRun, isPending: isTriggeringRun } =
    useTriggerPayrollRun(businessId, selectedPayPeriod?.id ?? null);
  const isIcmRunActionRunning =
    isCreatingPeriod || isTriggeringRun || hasActiveIcmPayrollRun;

  const canRerunWholePayroll = canRerunWholeWorkbook({
    business,
    hasEditableSheet,
  });
  const canStartInitialPayroll = canRunInitialPayroll(
    business,
    selectedAiRunId,
  );
  const hasIcmAccess = hasIcmPayrollViewerAccess(business);
  const hasPayrollScript = hasPayrollScriptAccess(business);

  const activePlanId = business?.active_icm_plan_id ?? null;
  const activePlanName = business?.active_icm_plan_name ?? null;
  const lastRunPlanId =
    selectedPayPeriod?.latest_completed_icm_run_plan_id ?? null;
  const lastRunPlanName =
    selectedPayPeriod?.latest_completed_icm_run_plan_name ?? null;
  const planMismatch =
    !!activePlanId && !!lastRunPlanId && activePlanId !== lastRunPlanId;
  const noPlanAvailable = !activePlanId && !lastRunPlanId;
  const icmRunDisabledReason = noPlanAvailable
    ? "No active ICM plan configured for this business"
    : isBeforeBusinessCreated
      ? "ICM runs can't be requested for periods before the business was created"
      : undefined;
  const { enabled: showIcmRerunControls } = useFeatureFlag(
    FEATURE_FLAG_NAMES.enable_icm_reruns,
  );

  const runTarget = getPayBreakdownRunTarget({
    selectedPeriod,
    selectedPeriodForCard,
    latestIcmRunStatus: icmLatestRunStatus,
    hasActiveRun,
    isLocked,
    isFuturePeriod,
    hasAiScript,
    hasIcmAccess,
    hasPayrollScript,
    icmRunDisabledReason,
    canRerunWholePayroll,
    canStartInitialPayroll,
    hasRerunUnits: rerunUnits.length > 0,
  });

  const handleTriggerAiRun = (
    runOptions?: AiScriptRerunRunOptions,
    chosenSheetNames?: string[],
  ) => {
    if (!selectedPeriod || isCreatingAiRun || isLocked || isFuturePeriod)
      return;
    if (hasBlockingRun && chosenSheetNames?.length) return;
    const sheetNames = chosenSheetNames?.length ? chosenSheetNames : null;
    if (hasActiveRun && !sheetNames) return;

    createAiRun(
      {
        startDate: selectedPeriod.period_start,
        endDate: selectedPeriod.period_end,
        aiScriptVersionId: runOptions?.aiScriptVersionId ?? null,
        payrollRosterId: runOptions?.payrollRosterId ?? null,
        sheetNames,
      },
      {
        onSuccess: (response) => {
          const label =
            sheetNames && sheetNames.length > 1
              ? `${sheetNames.length} tabs`
              : (sheetNames?.[0] ?? "Payroll");
          if (response.coalesced) {
            toast.info(`${label} is already running or queued`);
          } else if (response.run.status === "pending" && hasActiveRun) {
            toast.success(
              `${label} queued. It will start when the current run finishes`,
            );
          } else if (
            sheetNames ||
            output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.none
          ) {
            toast.success(
              sheetNames ? `${label} rerun started` : "Payroll run started",
            );
          }
        },
        onError: (error: unknown) => {
          toast.error("Failed to start payroll run", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleRequestAiRerun = () => {
    if (!selectedPeriod || isCreatingAiRun || isLocked || isFuturePeriod)
      return;

    setAiScriptRerunConfirmOpen(true);
  };

  const submitIcmRun = (icmPlanId?: string) => {
    if (!selectedPeriodForCard || isFuturePeriod) return;

    const callbacks = {
      onSuccess: () => toast.success("ICM calculation started"),
      onError: (error: unknown) =>
        toast.error("Failed to start calculation", {
          description: getErrorMessage(error),
        }),
    };

    if (!selectedPeriodForCard.existsInDb) {
      createPeriod(
        {
          start_date: selectedPeriodForCard.start_date,
          end_date: selectedPeriodForCard.end_date,
        },
        callbacks,
      );
      return;
    }

    triggerRun(
      {
        run_type: PAYROLL_RUN_TYPE.icm,
        ...(icmPlanId ? { icm_plan_id: icmPlanId } : {}),
      },
      callbacks,
    );
  };

  const handleRunIcm = () => {
    if (!selectedPeriodForCard) return;

    if (planMismatch) {
      setPlanPickerOpen(true);
      return;
    }

    if (!activePlanId && lastRunPlanId) {
      submitIcmRun(lastRunPlanId);
      return;
    }

    submitIcmRun();
  };

  const handleDownloadLegacyRun = () => {
    if (
      output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript ||
      !output.run.result_file_url
    ) {
      return;
    }

    downloadFile(output.run.result_file_url, getLegacyRunFilename(output.run));
  };

  const handleRunPreferredPayroll = () => {
    if (!runTarget.canRunPeriod) return;

    switch (runTarget.type) {
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript:
        if (output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) {
          handleRequestAiRerun();
          return;
        }
        handleTriggerAiRun();
        return;
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
        handleRunIcm();
        return;
      case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
        return;
    }
  };

  const rerunAction = getPayBreakdownRerunAction({
    runTarget,
    hasActiveRun,
    showIcmRerunControls: showIcmRerunControls && !isFuturePeriod,
    isCreatingAiRun,
    isCreatingPeriod,
    isTriggeringRun,
    isIcmRunActionRunning,
    onRunPreferredPayroll: handleRunPreferredPayroll,
  });

  return {
    aiScriptRerunConfirmOpen,
    setAiScriptRerunConfirmOpen,
    planPickerOpen,
    setPlanPickerOpen,
    isCreatingAiRun,
    isTriggeringRun,
    canRerunWholePayroll,
    activePlanId,
    activePlanName,
    lastRunPlanId,
    lastRunPlanName,
    icmRunDisabledReason,
    showIcmRerunControls,
    runTarget,
    handleTriggerAiRun,
    submitIcmRun,
    handleDownloadLegacyRun,
    handleRunPreferredPayroll,
    rerunAction,
  };
};
